import './columns-sidebar.scss';

import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CustomInput from 'components/CustomInput/customInput';
import CustomDatePicker from 'components/DatePicker';
import { FilterValues } from 'mobx/ColumnFilterStore';
import { useStore } from 'mobx/StoreContext';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { useFetchList } from 'services/common/react-query-common-service';
import { MASTER_ENDPOINT } from 'utils/Constant';

import InfiniteSearchSelect from './InfiniteSearchSelect/InfiniteSearchSelect';
import SidebarFooter from './SidebarFooter';
import StylishLoader from './StylishLoader/StylishLoader';

interface ColumnsSidebarProps {
  tableData?: any[];
  onApplyFilters?: (filters: FilterValues) => void;
  onResetFilters?: () => void;
}

const ColumnsSidebar: React.FC<ColumnsSidebarProps> = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  tableData = [],
  onApplyFilters,
  onResetFilters,
}) => {
  const { columnFilterStore } = useStore();
  const { filterValues } = columnFilterStore;
  const [isOpen, setIsOpen] = useState(false);
  const [enableWorkStatusFetch, setEnableWorkStatusFetch] = useState(false);

  // Fetch work status data using React Query with lazy loading
  const { data: workStatusResponse, isLoading: isWorkStatusLoading } =
    useFetchList(['work-status'], `${MASTER_ENDPOINT.WorkStatus}?status=true`, {
      enabled: enableWorkStatusFetch,
      staleTime: 5 * 60 * 1000, // 5 minutes
    });

  const workStatusData = workStatusResponse?.data || [];

  const handleToggleSidebar = () => {
    setIsOpen((prev) => !prev);
  };

  const handleWorkStatusClick = () => {
    if (!enableWorkStatusFetch) {
      setEnableWorkStatusFetch(true);
    }
  };

  const handleFilterChange = (field: keyof FilterValues, value: any) => {
    columnFilterStore.setFilterValue(field, value);
  };

  const handleSearchChange = (
    event: React.MouseEvent<HTMLElement>,
    newAlignment: string,
  ) => {
    event.stopPropagation();
    if (newAlignment) {
      handleFilterChange('title', '');
      columnFilterStore.setTabSearch(newAlignment);
    }
  };

  const handleReset = () => {
    columnFilterStore.reset();
    if (onResetFilters) {
      onResetFilters();
    }
  };

  const handleApply = () => {
    columnFilterStore.apply();
    if (onApplyFilters) {
      onApplyFilters(filterValues);
    }
    setIsOpen(false);
  };

  return (
    <Box
      className={`columns-sidebar-root ${isOpen ? 'expanded' : ''}`}
      onClick={!isOpen ? handleToggleSidebar : undefined}>
      {!isOpen ? (
        <>
          <FilterListIcon className="columns-icon" />
          <Typography className="columns-text">Filters</Typography>
        </>
      ) : (
        <>
          <Box className="sidebar-header" onClick={handleToggleSidebar}>
            <FilterListIcon className="header-icon" />
            <Typography className="header-text">Filters</Typography>
          </Box>

          <form className="sidebar-content">
            <Grid container>
              {/* Rate Filter with Hourly/Salary Toggle */}
              <Grid item md={12} sm={12} className="px-12">
                <Accordion defaultExpanded>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="rate-content"
                    id="rate-header"
                    className="rate-row">
                    <label className="dra-f-title">Rate</label>

                    <ToggleButtonGroup
                      value={filterValues.tabSearch}
                      exclusive
                      onChange={handleSearchChange}
                      aria-label="text alignment">
                      <ToggleButton
                        value="1"
                        sx={{
                          background:
                            filterValues.tabSearch === '1'
                              ? '#242424 !important'
                              : 'rgba(118,118,128,0.24)',
                          border: '0px',
                          color: 'white !important',
                          fontSize: '13px',
                          fontWeight: 'bold',
                          height: '30px',
                          justifyContent: 'center',
                          paddingTop: '13px',
                          textTransform: 'capitalize',
                        }}>
                        Hourly
                      </ToggleButton>
                      <ToggleButton
                        value="2"
                        sx={{
                          background:
                            filterValues.tabSearch === '2'
                              ? '#242424 !important'
                              : 'rgba(118,118,128,0.24)',
                          border: '0px',
                          color: 'white !important',
                          fontSize: '13px',
                          fontWeight: 'bold',
                          height: '30px',
                          justifyContent: 'center',
                          paddingTop: '13px',
                          textTransform: 'capitalize',
                          width: '85px',
                        }}>
                        Salary
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </AccordionSummary>
                  <AccordionDetails className="accordian-padding">
                    {filterValues.tabSearch === '1' ? (
                      <Slider
                        track="inverted"
                        min={15}
                        step={1}
                        className="rate-slider"
                        max={150}
                        aria-labelledby="hourly-rate-slider"
                        valueLabelDisplay="auto"
                        value={[
                          parseInt(filterValues.payRangeFrom) || 15,
                          parseInt(filterValues.payRangeTo) || 150,
                        ]}
                        onChange={(e, data) => {
                          const [from, to] = data as number[];
                          columnFilterStore.setFilterValues({
                            payRangeFrom: from.toString(),
                            payRangeTo: to.toString(),
                          });
                        }}
                        marks={[
                          {
                            label: '$15',
                            value: 15,
                          },
                          {
                            label: '$150',
                            value: 150,
                          },
                        ]}
                      />
                    ) : (
                      <Box className="rate-description">
                        <CustomInput
                          placeHolder="$100,000"
                          name="salarystart"
                          className="custominput"
                          value={'$ ' + filterValues.salaryFrom}
                          onChange={(e: any) => {
                            handleFilterChange(
                              'salaryFrom',
                              Number(e?.target?.value.replace(/[^0-9]+/g, '')),
                            );
                          }}
                        />
                        <CustomInput
                          placeHolder="$125,000"
                          name="salaryend"
                          className="custominput"
                          value={'$ ' + filterValues.salaryTo}
                          onChange={(e: any) => {
                            handleFilterChange(
                              'salaryTo',
                              Number(e?.target?.value.replace(/[^0-9]+/g, '')),
                            );
                          }}
                        />
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
                <Typography className="border" />
              </Grid>

              {/* Skills Filter */}
              <Grid item md={12} sm={12}>
                <Typography className="row-pad">
                  <label className="dra-f-title">Skills</label>
                  <InfiniteSearchSelect
                    endpoint={MASTER_ENDPOINT.Skills}
                    placeholder="Select Skills"
                    queryKey="skills-infinite-list"
                    label=""
                    value={''}
                    onChange={() => {}} // Picker behavior: value stays empty
                    onSelect={(opt) => {
                      const skillObj = { id: opt.value, name: opt.label };
                      const fIdx = filterValues.skillData.findIndex(
                        (item) => item.id === skillObj.id,
                      );
                      if (fIdx === -1) {
                        handleFilterChange('skillData', [
                          ...filterValues.skillData,
                          skillObj,
                        ]);
                      }
                    }}
                    mapResponse={(data) =>
                      data.data.map((item: any) => ({
                        label: String(item.name),
                        value: String(item.id),
                      }))
                    }
                  />
                  <Typography
                    component={'div'}
                    className="d-flex-a"
                    sx={{ flexWrap: 'wrap' }}>
                    {filterValues.skillData && filterValues.skillData.length ? (
                      filterValues.skillData.map((item: any, index: number) => {
                        return (
                          <Typography className="search-item" key={index}>
                            {item?.name}
                            <Typography
                              onClick={() => {
                                handleFilterChange(
                                  'skillData',
                                  filterValues.skillData.filter(
                                    (row: any) => row.id !== item?.id,
                                  ),
                                );
                              }}>
                              <CloseIcon />
                            </Typography>
                          </Typography>
                        );
                      })
                    ) : (
                      <Typography />
                    )}
                  </Typography>
                </Typography>
              </Grid>

              {/* Location Filter */}
              <Grid item md={12} sm={12}>
                <Typography className="row-pad">
                  <label className="dra-f-title">Location</label>
                  <CustomInput
                    placeHolder=""
                    onChange={(e) =>
                      handleFilterChange('location', e?.target?.value)
                    }
                    value={filterValues.location}
                    className={'custominput'}
                  />
                </Typography>
              </Grid>

              {/* Job Title Filter */}
              <Grid item md={12} sm={12}>
                <Typography className="row-pad">
                  <label className="dra-f-title">Job Title</label>
                  <CustomInput
                    placeHolder=""
                    onChange={(e) =>
                      handleFilterChange('title', e?.target?.value)
                    }
                    value={filterValues.title}
                    className={'custominput'}
                  />
                </Typography>
              </Grid>

              {/* Work Status Filter */}
              <Grid item md={12} sm={12}>
                <Typography className="row-pad">
                  <label className="dra-f-title">Work Status</label>
                  <FormControl fullWidth>
                    <Select
                      displayEmpty
                      value={filterValues.workStatus}
                      onChange={(e) =>
                        handleFilterChange('workStatus', e?.target?.value)
                      }
                      onFocus={handleWorkStatusClick}
                      className="sidebar-select"
                      MenuProps={{ className: 'sidebar-menu' }}
                      renderValue={(selected) => {
                        if (!selected) {
                          return (
                            <Typography className="select-placeholder">
                              Select Work Status
                            </Typography>
                          );
                        }
                        return (
                          workStatusData.find((opt: any) => opt.id === selected)
                            ?.name || selected
                        );
                      }}>
                      <MenuItem disabled value="">
                        Select Work Status
                      </MenuItem>
                      {isWorkStatusLoading ? (
                        <MenuItem
                          disabled
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            width: '100%',
                          }}>
                          <StylishLoader fullPage={false} mode="inline" />
                        </MenuItem>
                      ) : (
                        workStatusData.map((opt: any) => (
                          <MenuItem key={opt.id} value={opt.id}>
                            {opt.name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </Typography>
              </Grid>

              {/* Job Source Filter */}
              <Grid item md={12} sm={12}>
                <Typography className="row-pad">
                  <label className="dra-f-title">Job Source</label>
                  <InfiniteSearchSelect
                    endpoint={MASTER_ENDPOINT.JobSources}
                    placeholder="Select Job Source"
                    queryKey="job-source-infinite-list"
                    label=""
                    multiple={true}
                    value={filterValues.jobSourceId}
                    onChange={(val) => handleFilterChange('jobSourceId', val)}
                    extraParams="status=true"
                  />
                </Typography>
              </Grid>

              {/* Sales Person Filter */}
              <Grid item md={12} sm={12}>
                <Typography className="row-pad">
                  <label className="dra-f-title">Sales Person</label>
                  <InfiniteSearchSelect
                    endpoint={MASTER_ENDPOINT.Admins}
                    placeholder="Select Sales Person"
                    queryKey="sales-person-infinite-list"
                    label=""
                    value={filterValues.assignedTo}
                    onChange={(val) => handleFilterChange('assignedTo', val)}
                    extraParams="sortby=name&order=ASC&roleId=5&organizationId=&status=true"
                    mapResponse={(data) =>
                      data.data.map((item: any) => ({
                        label: `${String(item.firstName)} ${String(
                          item.lastName,
                        )}`,
                        value: String(item.id),
                      }))
                    }
                  />
                  <Typography className="border" />
                </Typography>
              </Grid>

              {/* Created by Filter */}
              <Grid item md={12} sm={12}>
                <Typography className="row-pad">
                  <label className="dra-f-title">Created by</label>
                  <InfiniteSearchSelect
                    endpoint={MASTER_ENDPOINT.Admins}
                    placeholder="Select Created By"
                    queryKey="created-by-infinite-list"
                    label=""
                    value={filterValues.assignedBy}
                    onChange={(val) => handleFilterChange('assignedBy', val)}
                    extraParams="sortby=name&order=ASC&organizationId=&status=true"
                    mapResponse={(data) =>
                      data.data.map((item: any) => ({
                        label: `${String(item.firstName)} ${String(
                          item.lastName,
                        )}`,
                        value: String(item.id),
                      }))
                    }
                  />
                </Typography>
              </Grid>

              {/* Date Range Filters */}
              <Grid item md={12} sm={12}>
                <Typography className="row-pad">
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={6}>
                      <label className="dra-f-title">From Date</label>
                      <CustomDatePicker
                        className="custom-datepicker"
                        maxDate={new Date()}
                        minDate={undefined}
                        value={filterValues.fromDate || null}
                        onChange={(dateValue: string) => {
                          if (
                            dateValue &&
                            filterValues.toDate &&
                            dateValue > filterValues.toDate
                          ) {
                            columnFilterStore.setFilterValues({
                              fromDate: dateValue,
                              toDate: dateValue,
                            });
                          } else {
                            handleFilterChange('fromDate', dateValue);
                          }
                        }}
                        disabled={false}
                        isFormating={false}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <label className="dra-f-title">To Date</label>
                      <CustomDatePicker
                        className="custom-datepicker"
                        maxDate={new Date()}
                        minDate={
                          filterValues.fromDate
                            ? new Date(filterValues.fromDate)
                            : undefined
                        }
                        value={filterValues.toDate || null}
                        onChange={(dateValue: string) => {
                          if (
                            dateValue &&
                            filterValues.fromDate &&
                            dateValue < filterValues.fromDate
                          ) {
                            columnFilterStore.setFilterValues({
                              fromDate: dateValue,
                              toDate: dateValue,
                            });
                          } else {
                            handleFilterChange('toDate', dateValue);
                          }
                        }}
                        disabled={false}
                        isFormating={false}
                      />
                    </Grid>
                  </Grid>
                </Typography>
                <Typography className="border mt-50" />
              </Grid>
            </Grid>
          </form>
          <SidebarFooter onReset={handleReset} onApply={handleApply} />
        </>
      )}
    </Box>
  );
};

export default observer(ColumnsSidebar);
